<script lang="ts">
  /*
   * The chrome every signed-in page sits in: the estate app bar, the account navigation, the content.
   *
   * The nav is the Website's _AccountMenu — same five destinations, same order, because it is the one
   * piece of this a returning customer already knows. Sign out has LEFT it and now lives in the app
   * bar's account menu, where it is on the support site; having it in both places would be two answers
   * to one question, and the bar's is the one that is the same everywhere.
   */
  import type { Snippet } from 'svelte'
  import { NAV_ROUTES } from '../routes'
  import { router } from '../router.svelte'
  import AppBar from './AppBar.svelte'

  let { title, children }: { title: string; children: Snippet } = $props()
</script>

<a class="skip" href="#content">Skip to content</a>

<AppBar />

<div class="wrap layout">
  <nav aria-label="Account">
    <ul>
      {#each NAV_ROUTES as route (route.name)}
        {@const current = router.route.name === route.name}
        <li>
          <a href={route.path} class="rip" class:current aria-current={current ? 'page' : undefined}>
            <!-- The label is rendered twice: the hidden bold copy reserves the width, so becoming the
                 current section cannot make the item wider and shuffle the column. The support site's
                 nav does the same thing for the same reason. -->
            <span class="label">{route.nav}</span>
            <span class="reserve" aria-hidden="true">{route.nav}</span>
          </a>
        </li>
      {/each}
    </ul>
  </nav>

  <main id="content">
    <h1>{title}</h1>
    {@render children()}
  </main>
</div>

<style>
  .skip {
    position: absolute;
    left: -9999px;
  }
  .skip:focus {
    left: var(--space-4);
    top: var(--space-4);
    z-index: 60;
    background: var(--bj-surface);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--corner-sm);
    box-shadow: var(--elev-2);
  }

  .layout {
    display: grid;
    gap: var(--space-6);
    padding-top: var(--space-6);
    padding-bottom: var(--space-12);
    align-items: start;
  }
  @media (min-width: 52rem) {
    .layout {
      grid-template-columns: 16rem 1fr;
      gap: var(--space-10);
    }
  }

  nav ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-1);
  }
  @media (min-width: 52rem) {
    nav ul {
      flex-direction: column;
      flex-wrap: nowrap;
      position: sticky;
      /* Clears the sticky app bar, so a sticky sidebar does not slide underneath it. */
      top: calc(69px + var(--space-6));
    }
  }

  nav a {
    display: grid;
    align-items: center;
    min-height: var(--touch);
    padding: 0 var(--space-4);
    border-radius: var(--corner-full);
    color: var(--bj-on-surface-variant);
    font-size: 0.95rem;
  }
  nav a:hover {
    text-decoration: none;
  }
  nav a.current {
    background: var(--secondary-container);
    color: var(--on-primary-container);
    font-weight: 600;
  }
  /* Both copies occupy the same cell; the reserve is invisible but keeps the bold width. */
  .label,
  .reserve {
    grid-area: 1 / 1;
    white-space: nowrap;
  }
  .reserve {
    font-weight: 600;
    visibility: hidden;
    pointer-events: none;
  }

  main {
    min-width: 0;
  }
  h1 {
    font-size: 1.6rem;
    margin-bottom: var(--space-6);
  }
</style>
